import { useState, useRef } from "react";
import { useSearch } from "wouter";
import { motion } from "framer-motion";
import { useReducedMotion } from "@/hooks/use-reduced-motion";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getCsrfToken } from "@workspace/api-client-react";
import { LinkedInIcon, DiscordIcon, GoogleIcon } from "@/components/provider-icons";

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
      <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const LAST_AUTH_PROVIDER_KEY = "carry_last_auth_provider";
type ProviderId = "linkedin" | "discord" | "google";

interface Provider {
  id: ProviderId;
  label: string;
  baseHref: string;
  icon: React.ReactNode;
}

const PROVIDERS: Provider[] = [
  {
    id: "linkedin",
    label: "Continue with LinkedIn",
    baseHref: "/api/auth/linkedin",
    icon: <LinkedInIcon className="w-5 h-5 text-[#0A66C2] shrink-0" />,
  },
  {
    id: "google",
    label: "Continue with Google",
    baseHref: "/api/auth/google",
    icon: <GoogleIcon className="w-5 h-5 shrink-0" />,
  },
  {
    id: "discord",
    label: "Continue with Discord",
    baseHref: "/api/auth/discord",
    icon: <DiscordIcon className="w-5 h-5 text-[#5865F2] shrink-0" />,
  },
];

function readLastProvider(): ProviderId | null {
  try {
    const v = localStorage.getItem(LAST_AUTH_PROVIDER_KEY);
    if (v === "linkedin" || v === "discord" || v === "google") return v;
    return null;
  } catch {
    return null;
  }
}

function writeLastProvider(id: ProviderId): void {
  try {
    localStorage.setItem(LAST_AUTH_PROVIDER_KEY, id);
  } catch {}
}

function sortProviders(lastUsed: ProviderId | null): Provider[] {
  if (!lastUsed) return PROVIDERS;
  const idx = PROVIDERS.findIndex((p) => p.id === lastUsed);
  if (idx <= 0) return PROVIDERS;
  const reordered = [...PROVIDERS];
  const [match] = reordered.splice(idx, 1);
  reordered.unshift(match);
  return reordered;
}

function buildHref(baseHref: string, returnTo: string | undefined): string {
  if (!returnTo) return baseHref;
  return `${baseHref}?returnTo=${encodeURIComponent(returnTo)}`;
}

export default function LoginPage() {
  useDocumentTitle("Sign In");
  const isReduced = useReducedMotion();
  const searchString = useSearch();

  const params = new URLSearchParams(searchString);
  const returnTo = params.get("returnTo") ?? undefined;

  const [lastUsed, setLastUsed] = useState<ProviderId | null>(() => readLastProvider());
  const [recruiterEmail, setRecruiterEmail] = useState("");
  const [recruiterSent, setRecruiterSent] = useState(false);
  const [recruiterLoading, setRecruiterLoading] = useState(false);
  const [recruiterError, setRecruiterError] = useState<string | null>(null);
  const emailRef = useRef<HTMLInputElement>(null);

  const providers = sortProviders(lastUsed);

  function handleProviderClick(id: ProviderId) {
    writeLastProvider(id);
    setLastUsed(id);
  }

  async function handleRecruiterSubmit(e: React.FormEvent) {
    e.preventDefault();
    const email = recruiterEmail.trim();
    if (!email) return;

    setRecruiterLoading(true);
    setRecruiterError(null);

    try {
      const res = await fetch("/api/auth/recruiter/magic-link", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-csrf-token": getCsrfToken() ?? "",
        },
        body: JSON.stringify({ email }),
        credentials: "include",
      });

      if (res.ok) {
        setRecruiterSent(true);
      } else {
        const body = await res.json().catch(() => ({})) as { error?: string };
        setRecruiterError(body.error ?? "Something went wrong. Please try again.");
      }
    } catch {
      setRecruiterError("Could not send the login link. Check your connection and try again.");
    } finally {
      setRecruiterLoading(false);
    }
  }

  return (
    <div className="flex-1 flex flex-col items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-primary/8 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-[0.03] pointer-events-none" />

      <motion.div
        className="relative z-10 w-full max-w-sm"
        initial={isReduced ? false : { opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: isReduced ? 0 : 0.45, ease: "easeOut" }}
      >
        <div className="text-center mb-8">
          <motion.p
            className="text-[10px] font-semibold uppercase tracking-[0.3em] text-primary/80 mb-3"
            initial={isReduced ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: isReduced ? 0 : 0.4, delay: isReduced ? 0 : 0.1 }}
          >
            Members Only
          </motion.p>
          <motion.h1
            className="font-display text-3xl font-bold tracking-tight text-white uppercase"
            initial={isReduced ? false : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: isReduced ? 0 : 0.4, delay: isReduced ? 0 : 0.15 }}
          >
            The Carry Network
          </motion.h1>
          <motion.p
            className="mt-2 text-sm text-zinc-400"
            initial={isReduced ? false : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: isReduced ? 0 : 0.4, delay: isReduced ? 0 : 0.2 }}
          >
            Sign in to access the alumni network.
          </motion.p>
        </div>

        <motion.div
          className="bg-zinc-900/80 backdrop-blur-xl border border-white/10 rounded-2xl p-6 shadow-2xl flex flex-col gap-5"
          initial={isReduced ? false : { opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: isReduced ? 0 : 0.45, delay: isReduced ? 0 : 0.25 }}
        >
          <div className="flex flex-col gap-2">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 px-0.5">
              Alumni — Carry Members
            </p>
            {providers.map((provider) => (
              <a
                key={provider.id}
                href={buildHref(provider.baseHref, returnTo)}
                onClick={() => handleProviderClick(provider.id)}
                className="flex items-center gap-3 w-full px-4 py-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 border border-white/5 hover:border-white/10 text-white font-medium text-sm transition-colors"
              >
                {provider.icon}
                {provider.label}
                {lastUsed === provider.id && (
                  <span className="ml-auto bg-zinc-700 text-zinc-400 text-[10px] uppercase px-2 py-0.5 rounded-full">
                    Last Used
                  </span>
                )}
              </a>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-white/8" />
            <span className="text-[10px] font-semibold uppercase tracking-widest text-zinc-600">or</span>
            <div className="flex-1 h-px bg-white/8" />
          </div>

          <div className="flex flex-col gap-2">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 px-0.5">
              Company Partners &amp; Recruiters
            </p>
            {recruiterSent ? (
              <div className="flex items-center gap-3 rounded-lg bg-emerald-950/40 border border-emerald-900/40 px-4 py-3">
                <CheckIcon className="w-5 h-5 text-emerald-400 shrink-0" />
                <p className="text-sm text-emerald-300 font-medium">
                  Login link sent — check your inbox.
                </p>
              </div>
            ) : (
              <form onSubmit={handleRecruiterSubmit} className="flex flex-col gap-2">
                <Input
                  ref={emailRef}
                  type="email"
                  placeholder="you@company.com"
                  value={recruiterEmail}
                  onChange={(e) => setRecruiterEmail(e.target.value)}
                  className="bg-zinc-800 border-white/10 text-white placeholder:text-zinc-500 focus-visible:ring-primary/50 h-10 text-sm"
                  disabled={recruiterLoading}
                  autoComplete="email"
                />
                {recruiterError && (
                  <p className="text-xs text-red-400">{recruiterError}</p>
                )}
                <Button
                  type="submit"
                  disabled={recruiterLoading || !recruiterEmail.trim()}
                  className="w-full bg-primary hover:bg-primary/90 text-white font-bold tracking-wide uppercase text-xs h-10"
                >
                  {recruiterLoading ? "Sending..." : "Send Login Link"}
                </Button>
              </form>
            )}
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}
